import React, { useEffect, useState } from 'react';
import axios from 'axios';
import logo from '../assets/logo.png';

const STEPS = [
  { title: 'Refieres', text: 'Recomiendas el servicio de otro miembro a un prospecto tuyo.' },
  { title: 'Se cierra (o no)', text: 'El miembro referido reporta si logró cerrar el negocio.' },
  { title: 'Confirmas', text: 'Validas con tu prospecto que lo que reportaron es correcto.' },
  { title: 'Cobras tu comisión', text: 'Si hubo negocio, administración calcula y te informa tu comisión.' },
];

// Fondo/landing compartido entre Login y Registro — se mantiene fijo mientras se
// abre/cierra el modal encima, para que el contenido de la página no brinque.
export function AuthLanding({ children }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get('/api/public-stats').then(res => setStats(res.data)).catch(() => {});
  }, []);

  return (
    <div>
      <div className="login-screen">
        <img src={logo} alt="PYMES Networking HUB" className="login-logo" />
        <h1 className="sr-only">PYMES Networking HUB</h1>
        <p className="login-eyebrow">Red de referidos entre miembros</p>
        <p className="login-subtitle" style={{ maxWidth: '480px' }}>
          Recomienda servicios de otros miembros a tus contactos,
          y gana una comisión cuando se cierra el negocio.
        </p>

        {children}

        {stats && (
          <div className="login-stats">
            <div>
              <div className="login-stat-value">{stats.active_members}</div>
              <div className="login-stat-label">Miembros activos</div>
            </div>
            <div>
              <div className="login-stat-value">{stats.total_referrals}</div>
              <div className="login-stat-label">Referencias generadas</div>
            </div>
          </div>
        )}
      </div>

      <div className="page" style={{ paddingTop: '48px' }}>
        <h2 style={{ textAlign: 'center', fontSize: '22px', fontWeight: 800, marginBottom: '32px', letterSpacing: '-0.02em' }}>Cómo funciona</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
          {STEPS.map((s, i) => (
            <div key={i} className="card" style={{ textAlign: 'center', marginBottom: 0 }}>
              <div className="how-it-works-step">{String(i + 1).padStart(2, '0')}</div>
              <h3 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 6px' }}>{s.title}</h3>
              <p style={{ fontSize: '13px', color: 'var(--neutral-500)', margin: 0 }}>{s.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Input de contraseña con botón de ojito para mostrar/ocultar en texto plano — se le
// pasan las mismas props que a un <input> normal, tabIndex={-1} en el botón para que
// el tab del formulario no se detenga ahí.
export function PasswordInput({ value, onChange, name, required, minLength, autoFocus, placeholder }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="password-input-wrap">
      <input
        type={visible ? 'text' : 'password'}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        minLength={minLength}
        autoFocus={autoFocus}
        placeholder={placeholder}
        className="form-input"
      />
      <button
        type="button"
        className="password-toggle-btn"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
        tabIndex={-1}
      >
        {visible ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
            <path d="M1 1l22 22" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
    </div>
  );
}

// Checklist en vivo de los requisitos de contraseña — cada regla se marca en verde
// con un check en cuanto se cumple. Usado en Registro y Reset de contraseña; debe
// coincidir con PASSWORD_RE en server.js/Register.js/ResetPassword.js.
const PASSWORD_RULES = [
  { key: 'length', label: 'Mínimo 10 caracteres', test: (p) => p.length >= 10 },
  { key: 'upper', label: 'Una mayúscula', test: (p) => /[A-Z]/.test(p) },
  { key: 'lower', label: 'Una minúscula', test: (p) => /[a-z]/.test(p) },
  { key: 'digit', label: 'Un número', test: (p) => /\d/.test(p) },
  { key: 'symbol', label: 'Un símbolo', test: (p) => /[^\w\s]/.test(p) },
];

export function PasswordRequirements({ password }) {
  const value = password || '';
  return (
    <ul className="password-req-list">
      {PASSWORD_RULES.map((rule) => {
        const met = rule.test(value);
        return (
          <li key={rule.key} className={met ? 'password-req-met' : ''}>
            <span className="password-req-icon">{met ? '✓' : '•'}</span>
            {rule.label}
          </li>
        );
      })}
    </ul>
  );
}

// Modal blanco con blur atrás — usa los mismos estilos que el resto del sitio
// (.modal-backdrop / .modal-box), así el texto siempre tiene contraste correcto.
export function AuthModal({ title, onClose, children }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box auth-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="auth-modal-header">
          <h3 className="auth-modal-title">{title}</h3>
          <button type="button" className="auth-modal-close" onClick={onClose} aria-label="Cerrar">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}
